import { getFile, removeFile, storeFile } from '../OPFSStore.js';
import { addQueueRecord, getAllQueueRecords, getQueueRecord, removeQueueRecord, updateQueueRecordData } from './UploadQueueStore.js';
import { GeneratePushID } from '../../../modules/GeneratePushID.js';
import { GetExif } from '../../../modules/GetExif.js';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import posthog from 'posthog-js';
import { KatapultJob } from '../../../modules/katapult-job/KatapultJob.js';
import { composeUploadedPhotoDataUpdate } from '../../../modules/katapult-job/update-composers/KatapultPhotoUploadUpdateComposers.js';
import { checkTabLockIsStolen, getActiveLockAbortSignal, runWhileHoldingTabLock } from '../../../modules/TabLock.js';

/** @import { AssociationConfig, PhotoUpdate, AssociatedPhotoUpdate, Metadata as PhotoMetadata, PhotoUploadFailure, QueueRecord } from '../PhotoUpload/UploadQueueStore.js' */

/** @typedef {Object} UploadProgress
 * @property {number} numPhotos -  Total number of photos being uploaded.
 * @property {number} numPhotosRemaining - Number of photos that have not yet been uploaded.
 * @property {number} numPhotosUploaded - Number of photos that have been fully uploaded.
 * @property {number} numPhotosFailed - Number of photos that have failed to upload.
 * @property {number} progress - Overall upload progress percentage (0-100).
 * @property {boolean} [complete] - Whether the upload is complete.
 * @property {?number} [completedAt] - Timestamp when the upload was completed, or null if not complete.
 * @property {?number} [incompleteAt] - Timestamp when the upload was marked incomplete (e.g. due to a new photo being added), or null if completed.
 * @property {boolean} [failed] - Whether the upload has failed.
 * @property {Record<string, { progress: number, complete: boolean, failed: boolean, metadata: QueueRecord['metadata'] }>} uploadProgressByRecord - Progress info for individual records.
 */

/**
 * @typedef {Object} AddPhotoToQueueOptions
 * @property {AssociationConfig} [association] - Configuration for associating the photo after upload.
 * @property {?AssociatedPhotoUpdate} [associatedPhotoUpdate]
 * @property {?PhotoUpdate} [photoUpdate]
 * @property {?PhotoMetadata} [metadata] - Additional metadata to store with the photo upload.
 */

/**
 * The progress of a photo upload as recorded on the photo on the server.
 * @typedef {object} UploadState
 * @property {boolean} [photo_data_saved] Whether the photo data reached the server, written by this app with the photo data.
 * @property {boolean} [photo_upload_finished] Whether the server marked the upload complete and counted it.
 */

// Number of leading photo bytes stored as the exif object
const EXIF_HEADER_BYTE_LENGTH = 131072;

// Photo folder and camera every mobile photo is uploaded into
const MOBILE_UPLOAD_PHOTO_FOLDER_CAMERA = Object.freeze({ folderId: 'mobile_upload', cameraId: 'default' });

// Error codes that block uploading until someone fixes access or the photo
const BLOCKING_ERROR_CODES = new Set(['storage/unauthorized', 'PERMISSION_DENIED', 'MissingStoredPhotoBytesError']);

// Failures in a row before a photo upload shows as failed
const FAILURE_LIMIT = 6;

// Failures in a row before a blocking error shows as failed
const BLOCKING_FAILURE_LIMIT = 2;

// Progress a photo upload shows once its photo is sent until the server finishes it
const SENT_PHOTO_UPLOAD_PROGRESS = 90;

// Delays before each retry in a row while failed photo uploads are under the failure limit, repeating the last one
const RETRY_DELAYS_BEFORE_FAILURE_LIMIT_IN_MILLISECONDS = [30000, 120000, 600000];

// Delay before a retry once every photo upload left reached the failure limit
const RETRY_DELAY_AFTER_FAILURE_LIMIT_IN_MILLISECONDS = 7200000;

// Processes the queued photo uploads one at a time, combining requests that wait to start
const processQueuedPhotoUploads = createCombiningSerialProcessor(processQueuedPhotoUploadsOnce);

// Retries in a row since processing last left nothing to retry
let consecutiveQueueRetryCount = 0;

// Timer for the next retry of the queued photo uploads
/** @type {ReturnType<typeof setTimeout>|undefined} */
let queueRetryTimeoutId;

// Disconnects for the running upload state listeners, keyed by queue record id
/** @type {Map<string, () => void>} */
const disconnectUploadStateListenerByRecordId = new Map();

// Marks the upload service as set up for this page
let hasInitializedPhotoUploadService = false;

// Whether the database reports this device as online
let isOnline = false;

// Progress listener configurations keyed by listener instance id
const uploadEventCallbacks = {};

// Upload progress for each job with nested node and section progress
/** @type {Record<string, UploadProgress & { uploadProgressByNode?: Record<string, UploadProgress>, uploadProgressBySection?: Record<string, UploadProgress> }>} */
const uploadProgressByJob = {};

// Tab lock held by the one tab sending the queued photo uploads
const PHOTO_UPLOAD_QUEUE_LOCK_NAME = 'photo-upload-queue';

/** Thrown when a photo's format isn't supported for upload.*/
export class UnsupportedPhotoFormatError extends Error {
  /** @param {string} [fileType] - The unsupported MIME type */
  constructor(fileType) {
    super(`Unsupported photo format "${fileType || 'unknown'}". Only JPEG images are supported for upload.`);
    this.name = 'UnsupportedPhotoFormatError';
  }
}

/** Thrown when a queued photo's bytes are no longer stored on the device. */
class MissingStoredPhotoBytesError extends Error {
  /** @param {string} recordId */
  constructor(recordId) {
    super(`No photo data found for queued photo with id ${recordId}`);
    this.name = 'MissingStoredPhotoBytesError';
  }
}

/**
 * Resume uploads on reconnect and sign in, report progress, and send them once per page.
 * @returns {Promise<void>} Resolves once the first processing of the queue ends.
 */
async function initializePhotoUploadService() {
  if (hasInitializedPhotoUploadService) return;
  hasInitializedPhotoUploadService = true;

  // Process the queued photo uploads again whenever the connection returns
  listenForReconnectToProcessQueuedPhotoUploads();

  // Process the queued photo uploads again whenever a user signs in
  listenForSignInToProcessQueuedPhotoUploads();

  // Report initial progress for any records in the queue
  await refreshPhotoUploadProgress();

  // Rebuild the progress and process the queue each time this tab becomes visible
  listenForTabVisibilityToRefreshProgressAndProcessQueuedPhotoUploads();

  // Process the photo uploads left in the queue from where each left off
  await processQueuedPhotoUploads();
}

/**
 * Track whether the device is online and process the queued photo uploads each time it comes back.
 */
function listenForReconnectToProcessQueuedPhotoUploads() {
  const database = firebase.database();
  const connectedReference = database.ref('.info/connected');
  connectedReference.on('value', (snapshot) => {
    // Track whether the device is online
    isOnline = snapshot.val() === true;
    if (!isOnline) return;

    // Process the queue again each time the device comes back online
    processQueuedPhotoUploads();
  });
}

/**
 * Refresh the photo upload progress and process the queue each time this tab becomes visible.
 */
function listenForTabVisibilityToRefreshProgressAndProcessQueuedPhotoUploads() {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    refreshPhotoUploadProgress();
    processQueuedPhotoUploads();
  });
}

/**
 * Process the queued photo uploads each time a user signs in.
 */
function listenForSignInToProcessQueuedPhotoUploads() {
  onAuthStateChanged(getAuth(), (user) => {
    if (!user) return;
    processQueuedPhotoUploads();
  });
}

/**
 * Generates a unique key for a section based on connectionId and sectionId.
 * @param {string} connectionId
 * @param {string} sectionId
 * @returns {string}
 */
function getSectionKey(connectionId, sectionId) {
  return `${connectionId}:${sectionId}`;
}

/**
 * Retrieves upload progress info for a specific job.
 * @param {string} jobId
 * @returns {UploadProgress}
 */
function getUploadInfoForJob(jobId) {
  const jobUploadInfo = uploadProgressByJob[jobId];
  if (jobUploadInfo) return structuredClone(jobUploadInfo);
  return getEmptyUploadProgress();
}

/**
 * Retrieves upload progress info for a specific node within a job.
 * @param {string} jobId
 * @param {string} nodeId
 * @returns {UploadProgress}
 */
function getUploadInfoForNode(jobId, nodeId) {
  const nodeUploadInfo = uploadProgressByJob[jobId]?.uploadProgressByNode?.[nodeId];
  if (nodeUploadInfo) return structuredClone(nodeUploadInfo);
  return getEmptyUploadProgress();
}

/**
 * Retrieves upload progress info for a specific section within a job.
 * @param {string} jobId
 * @param {string} connectionId
 * @param {string} sectionId
 * @returns {UploadProgress}
 */
function getUploadInfoForSection(jobId, connectionId, sectionId) {
  const sectionKey = getSectionKey(connectionId, sectionId);
  const sectionUploadInfo = uploadProgressByJob[jobId]?.uploadProgressBySection?.[sectionKey];
  if (sectionUploadInfo) return structuredClone(sectionUploadInfo);
  return getEmptyUploadProgress();
}

/**
 * Creates an upload event listener based on the provided configuration.
 * @param {Object} listenerConfig
 * @param {string} listenerConfig.jobId
 * @param {string} [listenerConfig.nodeId]
 * @param {string} [listenerConfig.connectionId]
 * @param {string} [listenerConfig.sectionId]
 * @param {(UploadProgress) => void} listenerConfig.callback
 * @returns {Function} A function to disconnect the listener.
 */
function createUploadEventListener(listenerConfig) {
  const callbackInstanceId = crypto.randomUUID();
  uploadEventCallbacks[callbackInstanceId] = listenerConfig;
  const disconnect = () => delete uploadEventCallbacks[callbackInstanceId];
  return disconnect;
}

/**
 * Listens to upload events for a specific job and invokes the callback with progress updates.
 * @param {string} jobId
 * @param {(UploadProgress) => void} callback
 * @returns {Function} A function to disconnect the listener.
 */
function listenToUploadEventsForJob(jobId, callback) {
  const disconnect = createUploadEventListener({ jobId, callback });
  const jobUploadInfo = getUploadInfoForJob(jobId);
  callback(jobUploadInfo);
  return disconnect;
}

/**
 * Listens to upload events for a specific node within a job and invokes the callback with progress updates.
 * @param {string} jobId
 * @param {string} nodeId
 * @param {(UploadProgress) => void} callback
 * @returns {Function} A function to disconnect the listener.
 */
function listenToUploadEventsForNode(jobId, nodeId, callback) {
  const disconnect = createUploadEventListener({ jobId, nodeId, callback });
  const nodeUploadInfo = getUploadInfoForNode(jobId, nodeId);
  callback(nodeUploadInfo);
  return disconnect;
}

/**
 * Listens to upload events for a specific section within a job and invokes the callback with progress updates.
 * @param {string} jobId
 * @param {string} connectionId
 * @param {string} sectionId
 * @param {(UploadProgress) => void} callback
 * @returns {Function} A function to disconnect the listener.
 */
function listenToUploadEventsForSection(jobId, connectionId, sectionId, callback) {
  const disconnect = createUploadEventListener({ jobId, connectionId, sectionId, callback });
  const sectionUploadInfo = getUploadInfoForSection(jobId, connectionId, sectionId);
  callback(sectionUploadInfo);
  return disconnect;
}

/**
 * Notifies all progress listeners relevant to the given recordId of the current upload progress.
 * @param {string} recordId
 * @returns {Promise<void>}
 */
async function notifyProgressListeners(recordId) {
  for (const listenerConfig of Object.values(uploadEventCallbacks)) {
    const record = await getQueueRecord(recordId);
    if (!record) continue;

    // Notify job listeners.
    if (listenerConfig.jobId === record.jobId && !listenerConfig.nodeId && !listenerConfig.connectionId && !listenerConfig.sectionId) {
      const jobUploadInfo = getUploadInfoForJob(record.jobId);
      listenerConfig.callback(jobUploadInfo);
    }

    // Notify node listeners.
    if (
      listenerConfig.jobId === record.jobId &&
      listenerConfig.nodeId &&
      record.association &&
      'nodeId' in record.association &&
      listenerConfig.nodeId === record.association.nodeId
    ) {
      const nodeUploadInfo = getUploadInfoForNode(record.jobId, listenerConfig.nodeId);
      listenerConfig.callback(nodeUploadInfo);
    }

    // Notify section listeners.
    if (
      listenerConfig.jobId === record.jobId &&
      listenerConfig.connectionId &&
      listenerConfig.sectionId &&
      record.association &&
      'connectionId' in record.association &&
      'sectionId' in record.association &&
      listenerConfig.connectionId === record.association.connectionId &&
      listenerConfig.sectionId === record.association.sectionId
    ) {
      const sectionUploadInfo = getUploadInfoForSection(record.jobId, listenerConfig.connectionId, listenerConfig.sectionId);
      listenerConfig.callback(sectionUploadInfo);
    }
  }
}

/**
 * Notify every progress listener of the progress it follows.
 */
function notifyAllProgressListeners() {
  for (const listenerConfiguration of Object.values(uploadEventCallbacks)) {
    const uploadProgress = getUploadProgressForListenerConfiguration(listenerConfiguration);
    listenerConfiguration.callback(uploadProgress);
  }
}

/**
 * Get the upload progress a listener follows for its node, section, or job.
 * @param {{jobId: string, nodeId?: string, connectionId?: string, sectionId?: string}} listenerConfiguration
 * @returns {UploadProgress} A copy of the followed upload progress.
 */
function getUploadProgressForListenerConfiguration(listenerConfiguration) {
  const { jobId, nodeId, connectionId, sectionId } = listenerConfiguration;
  if (nodeId) return getUploadInfoForNode(jobId, nodeId);
  if (connectionId && sectionId) return getUploadInfoForSection(jobId, connectionId, sectionId);
  return getUploadInfoForJob(jobId);
}

/**
 * Adds a photo to the upload queue.
 * @param {File} file
 * @param {string} jobId
 * @param {AddPhotoToQueueOptions} [options]
 * @returns {Promise<string>} The id of the added queue record.
 */
async function addPhotoToQueue(file, jobId, options) {
  if (!file) throw new Error('file is required to add a photo to the upload queue.');
  if (!jobId) throw new Error('jobId is required to add a photo to the upload queue.');

  const currentUserUid = getAuth().currentUser?.uid;
  if (!currentUserUid) throw new Error('User must be authenticated to add a photo to the upload queue.');

  // Read the photo's EXIF before anything is stored
  const exif = await GetExif(file);

  // Generate a unique photo id for this upload so that retries can reference the same photo.
  const photoId = crypto.randomUUID();
  const queueRecord = {
    jobId,
    photoId,
    filetype: file.type,
    filename: file.name,
    uploadedBy: currentUserUid,
    exif,
    metadata: options?.metadata,
    association: options?.association,
    associatedPhotoUpdate: options?.associatedPhotoUpdate,
    photoUpdate: options?.photoUpdate,
    hasUploadedPhotoBytes: false
  };

  // Store the photo bytes before adding the queue record that points to them
  const recordId = GeneratePushID();
  await storeFile('photo_uploads', recordId, file);
  await addQueueRecord(queueRecord, recordId).catch(async (error) => {
    // Remove the stored bytes when the queue record cannot be added
    await removeStoredPhotoBytes(recordId);
    throw error;
  });

  // Capture the photo joining the queue
  capturePhotoAddedToQueue(queueRecord, file);

  // Report the queued photo to progress listeners with zero progress
  await notifyPhotoUploadProgress(recordId, { progress: 0 });

  return recordId;
}

/**
 * Capture an analytics event for a photo being added to the upload queue.
 * @param {{jobId: string, photoId: string}} queueRecord
 * @param {File} file
 */
function capturePhotoAddedToQueue(queueRecord, file) {
  posthog.capture('photo_upload_added_to_queue', {
    photo_size_bytes: file.size,
    is_device_online: navigator.onLine,
    job_id: queueRecord.jobId,
    photo_id: queueRecord.photoId
  });
}

/**
 * Removes a photo from the upload queue and deletes its stored data.
 * @param {string} recordId
 * @param {Object} [options]
 * @param {boolean} [options.notifyRemoval=false] - Determines whether the removal should be reflected in the progress
 * @returns {Promise<void>}
 */
async function removePhotoFromQueue(recordId, options) {
  const notifyRemoval = options?.notifyRemoval ?? false;
  if (notifyRemoval) await notifyPhotoUploadProgress(recordId, { removed: true });

  // Stop listening to the photo's upload state
  disconnectUploadStateListenerByRecordId.get(recordId)?.();
  disconnectUploadStateListenerByRecordId.delete(recordId);

  // Remove the queue record before the photo bytes it points to
  await removeQueueRecord(recordId);
  await removeStoredPhotoBytes(recordId);
}

/**
 * Remove the stored photo bytes of a queue record, treating bytes already gone as removed.
 * @param {string} recordId
 * @returns {Promise<void>} Resolves once no photo bytes are stored for the record.
 */
async function removeStoredPhotoBytes(recordId) {
  try {
    await removeFile('photo_uploads', recordId);
  } catch (error) {
    // Ignore bytes that are already gone and rethrow any other failure
    if (error?.name !== 'NotFoundError') throw error;
  }
}

/**
 * Read the stored photo bytes of a queue record.
 * @param {string} recordId
 * @returns {Promise<Blob>} The stored photo bytes.
 */
async function readStoredPhotoBytes(recordId) {
  try {
    return await getFile('photo_uploads', recordId);
  } catch (error) {
    // Report missing bytes as permanent and rethrow any other failure
    if (error?.name === 'NotFoundError') throw new MissingStoredPhotoBytesError(recordId);
    throw error;
  }
}

/**
 * Creates an empty UploadProgress object.
 * @returns {UploadProgress}
 */
function getEmptyUploadProgress() {
  return {
    numPhotos: 0,
    numPhotosRemaining: 0,
    numPhotosUploaded: 0,
    numPhotosFailed: 0,
    progress: 0,
    complete: true,
    completedAt: Date.now(),
    incompleteAt: null,
    failed: false,
    uploadProgressByRecord: {}
  };
}

/**
 * Sets the progress for a specific record within the given progress info.
 * @param {UploadProgress} uploadProgress
 * @param {QueueRecord} record
 * @param {{ progress?: number, complete?: boolean, failed?: boolean, removed?: boolean }} newRecordProgress
 */
function setRecordProgressAndUpdateProgress(uploadProgress, record, newRecordProgress) {
  uploadProgress.uploadProgressByRecord ??= {};

  // If this upload progress is already complete (and has been for 1 second to avoid UI flicker), remove any progress info for completed records.
  if (uploadProgress.complete && uploadProgress.completedAt != null && Date.now() - uploadProgress.completedAt > 1000) {
    for (const otherRecordId in uploadProgress.uploadProgressByRecord) {
      const recordProgress = uploadProgress.uploadProgressByRecord[otherRecordId];
      if (recordProgress.complete) {
        delete uploadProgress.uploadProgressByRecord[otherRecordId];
      }
    }
  }

  // TODO (01/29/2026): Make this work a bit better
  if (newRecordProgress.removed) delete uploadProgress.uploadProgressByRecord[record.id];
  else {
    uploadProgress.uploadProgressByRecord[record.id] ??= {
      progress: 0,
      complete: false,
      failed: false,
      metadata: record.metadata,
      createdAt: record.createdAt
    };
    Object.assign(uploadProgress.uploadProgressByRecord[record.id], newRecordProgress);
  }

  const recordProgressArray = Object.values(uploadProgress.uploadProgressByRecord);
  const numPhotos = recordProgressArray.length;
  const numPhotosRemaining = recordProgressArray.filter((p) => !p.complete && !p.failed).length;
  const numPhotosUploaded = recordProgressArray.filter((p) => p.complete).length;
  const numPhotosFailed = recordProgressArray.filter((p) => p.failed).length;
  const recordProgressSum = recordProgressArray.filter((p) => !p.failed).reduce((sum, p) => sum + p.progress, 0);
  const progress = numPhotos - numPhotosFailed === 0 ? 0 : recordProgressSum / (numPhotos - numPhotosFailed);
  const complete = numPhotosRemaining == 0;

  const recordsWithCreatedAt = recordProgressArray.filter((p) => p.createdAt != null);
  // Get the timestamp of the most recent completed photo.
  const completedAt =
    recordsWithCreatedAt
      .filter((p) => p.complete)
      .map((p) => p.createdAt)
      .sort((a, b) => b - a)[0] ?? null;
  // Get the timestamp of the oldest incomplete photo.
  const incompleteAt =
    recordsWithCreatedAt
      .filter((p) => !p.complete)
      .map((p) => p.createdAt)
      .sort((a, b) => a - b)[0] ?? null;

  const failed = numPhotosFailed > 0;
  Object.assign(uploadProgress, {
    numPhotos,
    numPhotosRemaining,
    numPhotosUploaded,
    numPhotosFailed,
    progress,
    complete,
    completedAt,
    incompleteAt,
    failed
  });
}

/**
 * Manages and notifies photo upload progress by:
 * - Updating progress info structures.
 * - Notifying relevant listeners of progress updates.
 * - Cleaning up progress info structures when uploads complete.
 * @param {string} recordId
 * @param {{ progress?: number, complete?: boolean, failed?: boolean, removed?: boolean }} progress
 * @returns {Promise<void>} Resolves once listeners are notified, logging any failure instead of rejecting.
 */
async function notifyPhotoUploadProgress(recordId, progress) {
  try {
    const record = await getQueueRecord(recordId);
    if (!record) return;

    // Track the record's progress and notify the listeners following it
    setRecordProgressForJobNodeAndSection(record, progress);
    await notifyProgressListeners(recordId);
  } catch (error) {
    // Log the failure and leave the upload itself unaffected
    console.warn(`Reporting progress for photo upload ${recordId} failed.`, error);
  }
}

/**
 * Set a queue record's progress within the progress of its job, node, and section.
 * @param {QueueRecord} record
 * @param {{ progress?: number, complete?: boolean, failed?: boolean, removed?: boolean }} progress
 */
function setRecordProgressForJobNodeAndSection(record, progress) {
  // Track progress by job.
  const jobUploadInfo = (uploadProgressByJob[record.jobId] ??= getEmptyUploadProgress());
  // Reset job upload info if it is already complete.
  setRecordProgressAndUpdateProgress(jobUploadInfo, record, progress);

  // Track progress by node.
  if (record.association && 'nodeId' in record.association) {
    jobUploadInfo.uploadProgressByNode ??= {};
    const nodeUploadInfo = (jobUploadInfo.uploadProgressByNode[record.association.nodeId] ??= getEmptyUploadProgress());
    // Reset node upload info if it is already complete.
    setRecordProgressAndUpdateProgress(nodeUploadInfo, record, progress);
  }

  // Track progress by section.
  if (record.association && 'connectionId' in record.association && 'sectionId' in record.association) {
    jobUploadInfo.uploadProgressBySection ??= {};
    const sectionKey = getSectionKey(record.association.connectionId, record.association.sectionId);
    const sectionUploadInfo = (jobUploadInfo.uploadProgressBySection[sectionKey] ??= getEmptyUploadProgress());
    // Reset section upload info if it is already complete.
    setRecordProgressAndUpdateProgress(sectionUploadInfo, record, progress);
  }
}

/**
 * Determine the progress a queued photo upload has reached from its stored state.
 * @param {QueueRecord} photoUpload
 * @returns {{progress: number, failed: boolean}} The progress percentage and whether the upload shows as failed.
 */
function determineProgressFromQueueRecord(photoUpload) {
  // Report sent progress once the photo bytes are uploaded or its data is staged
  const hasSentPhoto = photoUpload.hasUploadedPhotoBytes === true || photoUpload.hasStagedPhotoData === true;
  const progress = hasSentPhoto ? SENT_PHOTO_UPLOAD_PROGRESS : 0;
  const failed = checkFailureLimitIsReached(photoUpload);
  return { progress, failed };
}

/**
 * Purge the upload progress of every job, rebuild it from the queue, and notify every listener.
 * @returns {Promise<void>} Resolves once listeners are notified, logging any failure instead of rejecting.
 */
export async function refreshPhotoUploadProgress() {
  try {
    // Read every queued photo upload before touching the progress
    const queuedPhotoUploads = await getAllQueueRecords();

    // Purge the progress of every job
    Object.keys(uploadProgressByJob).forEach((jobId) => delete uploadProgressByJob[jobId]);

    // Rebuild the progress from the stored state of each queued photo upload
    for (const photoUpload of queuedPhotoUploads) {
      const queuedPhotoUploadProgress = determineProgressFromQueueRecord(photoUpload);
      setRecordProgressForJobNodeAndSection(photoUpload, queuedPhotoUploadProgress);
    }

    // Notify every listener of the rebuilt progress
    notifyAllProgressListeners();
  } catch (error) {
    // Log the failure and leave the queue to report progress as it sends
    console.warn('Refreshing photo upload progress failed.', error);
  }
}

/**
 * Get every queued photo upload, including the ones that show as failed.
 * @returns {Promise<QueueRecord[]>} The queue records, oldest first.
 */
async function getSendableQueuedPhotoUploads() {
  // Read the whole queue oldest first
  const queuedPhotoUploads = await getAllQueueRecords();
  return queuedPhotoUploads.toSorted((firstPhotoUpload, secondPhotoUpload) => firstPhotoUpload.createdAt - secondPhotoUpload.createdAt);
}

/**
 * Check whether the device is online with a user signed in.
 * @returns {boolean} True when photo uploads can be sent.
 */
function checkDeviceIsOnlineAndSignedIn() {
  const isSignedIn = getAuth().currentUser != null;
  return isOnline && isSignedIn;
}

/**
 * Send every sendable photo upload round by round, stopping when offline, signed out, or the lock is stolen.
 * @param {ReadonlySet<string>} [attemptedRecordIds] Record ids already attempted in earlier rounds.
 * @returns {Promise<void>} Resolves once every sendable photo upload was attempted or sending stopped.
 */
async function sendPhotoUploads(attemptedRecordIds = new Set()) {
  // Find the sendable photo uploads not attempted in an earlier round
  const sendablePhotoUploads = await getSendableQueuedPhotoUploads();
  const photoUploadsToSend = sendablePhotoUploads.filter((photoUpload) => !attemptedRecordIds.has(photoUpload.id));
  if (photoUploadsToSend.length === 0) return;

  // Resume each photo upload in order and log any failure
  for (const photoUpload of photoUploadsToSend) {
    // Stop sending while offline, signed out, or after another tab steals the queue lock
    const isDeviceOnlineAndSignedIn = checkDeviceIsOnlineAndSignedIn();
    const isQueueLockStolen = checkTabLockIsStolen(PHOTO_UPLOAD_QUEUE_LOCK_NAME);
    if (!isDeviceOnlineAndSignedIn || isQueueLockStolen) return;

    try {
      await sendPhotoUpload(photoUpload.id);
    } catch (error) {
      console.warn(`Sending photo upload ${photoUpload.id} failed.`, error);
    }
  }

  // Send the photo uploads added while this round was sending
  const sentRecordIds = photoUploadsToSend.map((photoUpload) => photoUpload.id);
  const nextAttemptedRecordIds = new Set([...attemptedRecordIds, ...sentRecordIds]);
  await sendPhotoUploads(nextAttemptedRecordIds);
}

/**
 * Send every sendable photo upload unless another tab is sending them, then schedule a retry for any left.
 * @returns {Promise<void>} Resolves once sending stopped and any retry is scheduled, logging any failure instead of rejecting.
 */
async function processQueuedPhotoUploadsOnce() {
  // Replace any scheduled retry with this processing
  clearTimeout(queueRetryTimeoutId);

  try {
    // Leave processing to the reconnect and sign in listeners while offline or signed out
    const isDeviceOnlineAndSignedIn = checkDeviceIsOnlineAndSignedIn();
    if (!isDeviceOnlineAndSignedIn) return;

    // Send the photo uploads and leave the retry to whichever tab ends up holding the lock
    const { hasKeptLock } = await runWhileHoldingTabLock(PHOTO_UPLOAD_QUEUE_LOCK_NAME, sendPhotoUploads);
    if (!hasKeptLock) return;

    // Leave the retry to the reconnect and sign in listeners while offline or signed out
    const isRetryLeftToReconnectAndSignInListeners = !checkDeviceIsOnlineAndSignedIn();
    if (isRetryLeftToReconnectAndSignInListeners) return;

    // Find the photo uploads left in the queue that no upload state listener will process again
    const photoUploadsLeftInQueue = await getSendableQueuedPhotoUploads();
    const checkUploadStateListenerWillProcessQueueAgain = (photoUpload) => disconnectUploadStateListenerByRecordId.has(photoUpload.id);
    const photoUploadsNeedingRetry = photoUploadsLeftInQueue.filter(
      (photoUpload) => !checkUploadStateListenerWillProcessQueueAgain(photoUpload)
    );
    if (photoUploadsNeedingRetry.length === 0) {
      consecutiveQueueRetryCount = 0;
      return;
    }

    // Schedule a retry, waiting longer once every photo upload needing one shows as failed
    const shouldUseFinalFailureDelay = photoUploadsNeedingRetry.every(checkFailureLimitIsReached);
    scheduleQueuedPhotoUploadRetry(shouldUseFinalFailureDelay);
  } catch (error) {
    // Log the failure and schedule a retry
    console.warn('Processing the queued photo uploads failed and will be retried.', error);
    scheduleQueuedPhotoUploadRetry(false);
  }
}

/**
 * Schedule the next retry of the queued photo uploads after the delay for this retry in a row.
 * @param {boolean} shouldUseFinalFailureDelay Whether to wait the delay after the failure limit.
 */
function scheduleQueuedPhotoUploadRetry(shouldUseFinalFailureDelay) {
  // Pick the delay for this retry in a row, or the final failure delay
  const lastRetryDelayBeforeFailureLimitInMilliseconds = RETRY_DELAYS_BEFORE_FAILURE_LIMIT_IN_MILLISECONDS.at(-1);
  const retryDelayBeforeFailureLimitInMilliseconds =
    RETRY_DELAYS_BEFORE_FAILURE_LIMIT_IN_MILLISECONDS[consecutiveQueueRetryCount] ?? lastRetryDelayBeforeFailureLimitInMilliseconds;
  const retryDelayInMilliseconds = shouldUseFinalFailureDelay
    ? RETRY_DELAY_AFTER_FAILURE_LIMIT_IN_MILLISECONDS
    : retryDelayBeforeFailureLimitInMilliseconds;

  // Retry processing as the next retry in a row once the delay passes
  clearTimeout(queueRetryTimeoutId);
  queueRetryTimeoutId = setTimeout(() => {
    consecutiveQueueRetryCount += 1;
    processQueuedPhotoUploads();
  }, retryDelayInMilliseconds);
}

/**
 * Create a function that requests processing one at a time, joining a request to processing still waiting to start.
 * @param {() => Promise<void>} processOnce Processes once without rejecting.
 * @returns {() => Promise<void>} Requests processing and resolves once processing started after the request ends.
 */
function createCombiningSerialProcessor(processOnce) {
  // Track the latest requested processing and whether it has yet to start
  let latestProcessingPromise = Promise.resolve();
  let isLatestProcessingWaitingToStart = false;

  // Join the processing still waiting to start, or queue new processing behind the running one
  return () => {
    if (isLatestProcessingWaitingToStart) return latestProcessingPromise;
    isLatestProcessingWaitingToStart = true;
    latestProcessingPromise = latestProcessingPromise.then(() => {
      isLatestProcessingWaitingToStart = false;
      return processOnce();
    });
    return latestProcessingPromise;
  };
}

/**
 * Summarize a failed photo upload attempt together with the failures in a row before it.
 * @param {QueueRecord} photoUpload The photo upload as stored before this attempt failed.
 * @param {any} error
 * @param {number} failedAt When the attempt failed.
 * @returns {PhotoUploadFailure} The failure with this error and one more failure in a row.
 */
function summarizeFailedUploadAttempt(photoUpload, error, failedAt) {
  // Describe the error by its code and full message
  const code = error?.code ?? error?.name ?? 'unknown';
  const message = error?.message ?? String(error);

  // Add this failure to the count of failures in a row
  const previousFailureCount = photoUpload.uploadFailure?.consecutiveFailureCount ?? 0;
  const consecutiveFailureCount = previousFailureCount + 1;
  return { code, message, latestFailureAt: failedAt, consecutiveFailureCount };
}

/**
 * Check whether a photo upload has failed enough times in a row to reach its failure limit.
 * @param {QueueRecord} photoUpload
 * @returns {boolean} True when the failure limit is reached.
 */
function checkFailureLimitIsReached(photoUpload) {
  // Report no limit reached when nothing has failed
  const uploadFailure = photoUpload.uploadFailure;
  if (!uploadFailure) return false;

  // Check the failure limit, or the lower limit for a blocking error
  const hasReachedFailureLimit = uploadFailure.consecutiveFailureCount >= FAILURE_LIMIT;
  const isBlockingError = BLOCKING_ERROR_CODES.has(uploadFailure.code);
  const hasReachedBlockingFailureLimit = isBlockingError && uploadFailure.consecutiveFailureCount >= BLOCKING_FAILURE_LIMIT;
  return hasReachedFailureLimit || hasReachedBlockingFailureLimit;
}

/**
 * Record a failed photo upload attempt while online and report the attempt.
 * @param {string} recordId
 * @param {any} error
 * @returns {Promise<void>} Resolves once the attempt is reported, logging any failure to record it.
 */
async function recordPhotoUploadAttemptFailure(recordId, error) {
  try {
    // Report the attempt without recording it while offline or signed out
    const isDeviceOnlineAndSignedIn = checkDeviceIsOnlineAndSignedIn();
    if (!isDeviceOnlineAndSignedIn) return await notifyPhotoUploadProgress(recordId, { progress: 0 });

    // Stop when the photo upload already left the queue
    const storedPhotoUpload = await getQueueRecord(recordId);
    if (!storedPhotoUpload) return;

    // Store the failed attempt on the queue record
    const failedAt = Date.now();
    const nextUploadFailure = summarizeFailedUploadAttempt(storedPhotoUpload, error, failedAt);
    await updateQueueRecordData(recordId, { uploadFailure: nextUploadFailure });

    // Report the failed attempt and whether it reached the failure limit
    const failedPhotoUpload = { ...storedPhotoUpload, uploadFailure: nextUploadFailure };
    const isFailureLimitReached = checkFailureLimitIsReached(failedPhotoUpload);
    await notifyPhotoUploadProgress(recordId, { failed: isFailureLimitReached, progress: 0 });

    // Capture the attempt that first reaches the failure limit
    const hasAlreadyReachedFailureLimit = checkFailureLimitIsReached(storedPhotoUpload);
    if (isFailureLimitReached && !hasAlreadyReachedFailureLimit) capturePhotoUploadReachedFailureLimit(failedPhotoUpload);
  } catch (recordingError) {
    // Log the failure to record and leave the original error untouched
    console.warn(`Recording the failed attempt for photo upload ${recordId} failed.`, recordingError);
  }
}

/**
 * Resume a queued photo upload and continue it from where it left off.
 * @param {string} recordId
 * @returns {Promise<void>} Resolves once the photo upload is removed, found gone, or left waiting on the server.
 */
async function sendPhotoUpload(recordId) {
  // Stop when another tab already finished the photo upload
  const photoUpload = await getQueueRecord(recordId);
  if (!photoUpload) return;

  try {
    // Continue the photo upload from where it left off
    await resumeQueuedPhotoUpload(photoUpload);
  } catch (error) {
    // Leave a photo upload taken over by another tab for that tab to record
    const isQueueLockStolen = checkTabLockIsStolen(PHOTO_UPLOAD_QUEUE_LOCK_NAME);
    if (isQueueLockStolen) return;

    // Record the failed attempt and leave the photo upload queued
    await recordPhotoUploadAttemptFailure(recordId, error);
    throw error;
  }

  // Clear earlier failures once an attempt gets through
  const hasEarlierFailure = photoUpload.uploadFailure != null;
  if (!hasEarlierFailure) return;
  await updateQueueRecordData(recordId, { uploadFailure: null });
  await notifyPhotoUploadProgress(recordId, { failed: false });

  // Capture the recovery of a photo upload that had reached its failure limit
  const isRecoveringFromFailureLimit = checkFailureLimitIsReached(photoUpload);
  if (isRecoveringFromFailureLimit) capturePhotoUploadRecoveredAfterFailureLimit(photoUpload);
}

/**
 * Capture an analytics event for a photo upload reaching its failure limit.
 * @param {QueueRecord} failedPhotoUpload The photo upload with the failure that reached the limit.
 */
function capturePhotoUploadReachedFailureLimit(failedPhotoUpload) {
  const { uploadFailure } = failedPhotoUpload;
  posthog.capture('photo_upload_reached_failure_limit', {
    error_code: uploadFailure?.code,
    error_message: uploadFailure?.message,
    has_uploaded_photo_bytes: failedPhotoUpload.hasUploadedPhotoBytes === true,
    milliseconds_until_failure_limit: uploadFailure?.latestFailureAt - failedPhotoUpload.createdAt,
    job_id: failedPhotoUpload.jobId,
    photo_id: failedPhotoUpload.photoId
  });
}

/**
 * Capture an analytics event for a photo upload getting through after reaching its failure limit.
 * @param {QueueRecord} recoveredPhotoUpload The photo upload as stored before the attempt that got through.
 */
function capturePhotoUploadRecoveredAfterFailureLimit(recoveredPhotoUpload) {
  posthog.capture('photo_upload_recovered_after_failure_limit', {
    last_error_code: recoveredPhotoUpload.uploadFailure?.code,
    milliseconds_until_recovered: Date.now() - recoveredPhotoUpload.createdAt,
    job_id: recoveredPhotoUpload.jobId,
    photo_id: recoveredPhotoUpload.photoId
  });
}

/**
 * Continue a queued photo upload from where it left off, ending once it waits on the server.
 * @param {QueueRecord} queuedPhotoUpload
 * @returns {Promise<void>} Resolves once the photo upload is removed or a listener waits on the server for it.
 */
async function resumeQueuedPhotoUpload(queuedPhotoUpload) {
  // Hand a record queued by the old upload flow over to the current flow
  const isLegacyPhotoUpload = queuedPhotoUpload.hasUploadedPhotoBytes === undefined;
  const photoUpload = isLegacyPhotoUpload ? await handOffLegacyPhotoUpload(queuedPhotoUpload) : queuedPhotoUpload;

  // Leave the photo upload alone while its listener waits on the server
  if (disconnectUploadStateListenerByRecordId.has(photoUpload.id)) return;

  // Upload the photo bytes the record does not mark uploaded
  if (photoUpload.hasUploadedPhotoBytes !== true) await uploadPhotoBytesToStorage(photoUpload);

  // Read the photo's upload state from the server
  const uploadState = await readUploadStateFromServer(photoUpload);

  // Check whether the server has the photo data now or did on an earlier pass
  const isPhotoDataSavedOnServer = uploadState?.photo_data_saved === true;
  const isPhotoDataSavedAlreadyMarked = photoUpload.hasSeenPhotoDataOnServer === true;
  const hasSeenPhotoDataOnServer = isPhotoDataSavedOnServer || isPhotoDataSavedAlreadyMarked;

  // Mark the record the first time a pass sees the photo data on the server
  const isPhotoDataSavedNewlySeen = isPhotoDataSavedOnServer && !isPhotoDataSavedAlreadyMarked;
  if (isPhotoDataSavedNewlySeen) await updateQueueRecordData(photoUpload.id, { hasSeenPhotoDataOnServer: true });

  // Remove the photo upload once the server finished it or the photo was deleted from the server
  const isPhotoUploadFinished = uploadState?.photo_upload_finished === true;
  const isPhotoDeletedFromServer = hasSeenPhotoDataOnServer && uploadState == null;
  const isPhotoUploadDone = isPhotoUploadFinished || isPhotoDeletedFromServer;
  if (isPhotoUploadDone) return await removePhotoUploadFromDevice(photoUpload, isPhotoDeletedFromServer);

  // Stage the photo data when the server does not have it yet
  if (!isPhotoDataSavedOnServer) await stagePhotoData(photoUpload);

  // Process the queue again once the server finishes the upload or the photo is deleted from it
  listenForUploadStateToProcessQueuedPhotoUploads(photoUpload, hasSeenPhotoDataOnServer);
}

/**
 * Hand a photo upload queued by the old upload flow over to the current upload flow.
 * @param {QueueRecord} photoUpload
 * @returns {Promise<QueueRecord>} The queue record with every field the current upload flow sends it by.
 */
async function handOffLegacyPhotoUpload(photoUpload) {
  // Read the EXIF from the stored bytes under the photo's original name and type
  const photoData = await readStoredPhotoBytes(photoUpload.id);
  const photoFile = new File([photoData], photoUpload.filename, { type: photoUpload.filetype });
  const exif = await GetExif(photoFile);

  // Fill in every field the current upload flow sends the photo by
  /** @type {Partial<QueueRecord>} */
  const queueRecordDataUpdate = {
    photoId: photoUpload.photoId ?? photoUpload.id,
    exif,
    hasUploadedPhotoBytes: false
  };

  // Save the fields on the queue record and return the handed off record
  await updateQueueRecordData(photoUpload.id, queueRecordDataUpdate);
  return { ...photoUpload, ...queueRecordDataUpdate };
}

/**
 * Read a queued photo upload's upload state from the photo's record on the server.
 * @param {QueueRecord} photoUpload
 * @returns {Promise<UploadState|null>} The photo's upload state, or null when the server has none.
 */
async function readUploadStateFromServer(photoUpload) {
  // Read the main thread database because the worker shows staged writes before they land
  const serverJob = KatapultJob.fromId(photoUpload.jobId, { firebaseApp: globalThis.firebase });
  return await serverJob.getPhotoUploadState(photoUpload.photoId);
}

/**
 * Stage the photo data of a queued photo for the update manager to sync, skipping it when staging could repeat or revive the photo.
 * @param {QueueRecord} photoUpload
 * @returns {Promise<void>} Resolves once the photo data is staged or found not to need staging.
 */
async function stagePhotoData(photoUpload) {
  // Skip staging when the upload left the queue or its data was already staged or seen on the server
  const storedPhotoUpload = await getQueueRecord(photoUpload.id);
  const hasStoredPhotoUploadSeenPhotoData = storedPhotoUpload?.hasSeenPhotoDataOnServer === true;
  const hasStoredPhotoUploadStagedPhotoData = storedPhotoUpload?.hasStagedPhotoData === true;
  if (!storedPhotoUpload || hasStoredPhotoUploadSeenPhotoData || hasStoredPhotoUploadStagedPhotoData) return;

  // Skip staging when the server already has the photo data
  const uploadState = await readUploadStateFromServer(photoUpload);
  if (uploadState?.photo_data_saved === true) return;

  // Build the photo data marked saved in its upload state
  const uploadedPhoto = { ...photoUpload, ...MOBILE_UPLOAD_PHOTO_FOLDER_CAMERA };
  const uploadedPhotoDataUpdate = composeUploadedPhotoDataUpdate(uploadedPhoto);
  const photoDataSavedPath = `photos/${photoUpload.photoId}/upload_state/photo_data_saved`;
  const photoUploadUpdate = { ...uploadedPhotoDataUpdate, [photoDataSavedPath]: true };

  // Stage the update for the update manager
  const job = KatapultJob.fromId(photoUpload.jobId);
  await job.updateJobData(photoUploadUpdate);

  // Mark the photo data staged on the queue record for every tab on this device
  await updateQueueRecordData(photoUpload.id, { hasStagedPhotoData: true });

  // Report the photo data staged with sent progress
  await notifyPhotoUploadProgress(photoUpload.id, { progress: SENT_PHOTO_UPLOAD_PROGRESS });
}

/**
 * Process the queued photo uploads once the server finishes a photo upload or the photo is deleted from the server.
 * @param {QueueRecord} photoUpload
 * @param {boolean} hasSeenPhotoDataOnServer Whether the photo data was already seen on the server before listening.
 */
function listenForUploadStateToProcessQueuedPhotoUploads(photoUpload, hasSeenPhotoDataOnServer) {
  if (disconnectUploadStateListenerByRecordId.has(photoUpload.id)) return;

  // Register the disconnect before listening so a snapshot during setup can remove it
  let disconnectUploadStateListener = () => {};
  disconnectUploadStateListenerByRecordId.set(photoUpload.id, () => disconnectUploadStateListener());

  // Listen on the main thread database because the worker shows staged writes before they land
  const serverJob = KatapultJob.fromId(photoUpload.jobId, { firebaseApp: globalThis.firebase });
  let hasListenerSeenPhotoDataOnServer = hasSeenPhotoDataOnServer;
  let hasListenerFired = false;
  let recordingPhotoDataSeenOnServerPromise = Promise.resolve();
  disconnectUploadStateListener = serverJob.onPhotoUploadStateSnapshot(photoUpload.photoId, async (snapshot) => {
    if (hasListenerFired) return;

    // Record the photo data seen on the server the first time a snapshot shows it
    const uploadState = snapshot.val();
    const isPhotoDataSavedOnServer = uploadState?.photo_data_saved === true;
    const isPhotoDataNewlySeenOnServer = isPhotoDataSavedOnServer && !hasListenerSeenPhotoDataOnServer;
    if (isPhotoDataNewlySeenOnServer) {
      hasListenerSeenPhotoDataOnServer = true;
      const recordingPromise = updateQueueRecordData(photoUpload.id, { hasSeenPhotoDataOnServer: true });
      recordingPhotoDataSeenOnServerPromise = recordingPromise.catch((error) => console.warn('Recording photo data seen failed.', error));
    }

    // Wait until the server finishes the upload or the photo is deleted from the server
    const isPhotoUploadFinished = uploadState?.photo_upload_finished === true;
    const isPhotoDeletedFromServer = hasListenerSeenPhotoDataOnServer && uploadState == null;
    if (!isPhotoUploadFinished && !isPhotoDeletedFromServer) return;

    // Stop listening once setup returns
    hasListenerFired = true;
    disconnectUploadStateListenerByRecordId.delete(photoUpload.id);
    queueMicrotask(() => disconnectUploadStateListener());

    // Process the queue once the record shows the photo data seen
    await recordingPhotoDataSeenOnServerPromise;
    processQueuedPhotoUploads();
  });
}

/**
 * Report a queued photo upload complete, or removed when its photo was deleted, and remove it from the device.
 * @param {QueueRecord} photoUpload
 * @param {boolean} isPhotoDeletedFromServer Whether the photo was deleted from the server before the upload finished.
 * @returns {Promise<void>} Resolves once the photo upload is removed.
 */
async function removePhotoUploadFromDevice(photoUpload, isPhotoDeletedFromServer) {
  // Report the upload complete unless its photo was deleted, then remove it from the queue
  if (!isPhotoDeletedFromServer) await notifyPhotoUploadProgress(photoUpload.id, { complete: true, progress: 100 });
  await removePhotoFromQueue(photoUpload.id, { notifyRemoval: isPhotoDeletedFromServer });
}

/**
 * Upload the exif and full image Storage objects of a queued photo and mark the record uploaded.
 * @param {QueueRecord} photoUpload
 * @returns {Promise<void>} Resolves once both objects are stored under the photo id and the record marks them done.
 */
async function uploadPhotoBytesToStorage(photoUpload) {
  // Report zero progress and read the stored bytes for the photo upload
  await notifyPhotoUploadProgress(photoUpload.id, { progress: 0 });
  const photoData = await readStoredPhotoBytes(photoUpload.id);

  // Upload the exif header under the photo id
  const storage = firebase.storage();
  const exifHeaderData = photoData.slice(0, EXIF_HEADER_BYTE_LENGTH);
  const exifStorageReference = storage.ref(`photos/${photoUpload.photoId}_exif`);
  const exifUploadTask = exifStorageReference.put(exifHeaderData);
  await uploadUntilLockIsStolen(exifUploadTask);

  // Upload the full image under the photo id and report its progress
  const fullImageStorageReference = storage.ref(`photos/${photoUpload.photoId}_full.jpg`);
  const fullImageUploadTask = fullImageStorageReference.put(photoData);
  fullImageUploadTask.on('state_changed', (snapshot) => notifyFullImageUploadProgress(photoUpload.id, snapshot));
  await uploadUntilLockIsStolen(fullImageUploadTask);

  // Mark the record uploaded
  await updateQueueRecordData(photoUpload.id, { hasUploadedPhotoBytes: true });
}

/**
 * Wait for a Storage upload task, cancelling it when another tab steals the queue lock.
 * @param {PromiseLike<unknown> & {cancel: () => boolean}} uploadTask
 * @returns {Promise<void>} Resolves once the upload finishes, or rejects when it fails or is cancelled.
 */
async function uploadUntilLockIsStolen(uploadTask) {
  // Cancel the upload now or whenever another tab steals the queue lock
  const queueLockAbortSignal = getActiveLockAbortSignal(PHOTO_UPLOAD_QUEUE_LOCK_NAME);
  const cancelUploadTask = () => uploadTask.cancel();
  if (queueLockAbortSignal?.aborted) cancelUploadTask();
  queueLockAbortSignal?.addEventListener('abort', cancelUploadTask, { once: true });

  // Wait for the upload and stop listening for the steal once it settles
  try {
    await uploadTask;
  } finally {
    queueLockAbortSignal?.removeEventListener('abort', cancelUploadTask);
  }
}

/**
 * Report how far the full image of a queued photo has uploaded.
 * @param {string} recordId
 * @param {{bytesTransferred: number, totalBytes: number}} snapshot
 */
function notifyFullImageUploadProgress(recordId, snapshot) {
  const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * SENT_PHOTO_UPLOAD_PROGRESS);
  notifyPhotoUploadProgress(recordId, { progress });
}

/**
 * Adds a photo to the upload queue and request it be uploaded (if offline, upload will be deferred).
 * @param {File} file - The photo data to be uploaded.
 * @param {string} jobId - The id of the job to which the photo should be uploaded.
 * @param {AddPhotoToQueueOptions} [options] - Additional upload options.
 * @returns {Promise<string>} The id of the queue record once the photo is saved on the device.
 */
async function uploadPhoto(file, jobId, options) {
  if (file.type !== 'image/jpeg') {
    throw new UnsupportedPhotoFormatError(file.type);
  }

  // If this file is named "image.jpg" (the default name iOS gives to photos), give it a unique name.
  // Otherwise this will result in multiple photos having the same name which is an issue for GetExif() which builds
  // out a name_date string  based on the file name and uses it to determine if the photo already has a key in firebase.
  if (file.name === 'image.jpg') {
    file = new File([file], crypto.randomUUID() + '.jpg', { type: file.type, lastModified: file.lastModified });
  }

  // Save the photo to the queue
  const recordId = await addPhotoToQueue(file, jobId, options);

  // Process the queue without waiting on it
  processQueuedPhotoUploads();
  return recordId;
}

export {
  initializePhotoUploadService,
  uploadPhoto,
  listenToUploadEventsForJob,
  listenToUploadEventsForNode,
  listenToUploadEventsForSection,
  removePhotoFromQueue
};
